import { useSelector } from 'react-redux';
import { getCreativesActionProgress } from 'packages/store/creatives/creatives.selectors';
import {
    getImportActionProgress,
    getImportedTemplate,
    getImportTemplateAnalysis,
    getImportTemplateErrors,
    getImportTemplateTargetDpi,
    getImportTemplateType,
    getImportTemplateValidationErrors,
} from 'packages/store/importTemplate/importTemplate.selectors';
import { getNewDesignPage, getSelectedBlankType } from 'packages/store/newDesign/newDesign.selectors';
import { Template } from 'packages/store/templates/types';
import { ImportedTemplate, ImportType } from 'packages/store/importTemplate/types';
import { isIndesignDesignFileAnalysis, isScaledArtboardOverMaxSize } from 'packages/store/importTemplate/utils';
import { selectedTemplateSelector } from 'packages/store/templates/templates.selectors';
import { selectedGallerySelector } from 'packages/store/gallery/gallery.selectors';
import { getFigmaState } from 'packages/store/figma/figma.selectors';
import { envVar } from 'packages/helpers/envVar';
import { DesignSubPages } from './components/Sidebar/types';

const useCreationDisabledMap = () => {
    const activePage = useSelector(getNewDesignPage);
    const selectedType = useSelector(getSelectedBlankType);
    const selectedTemplate: Template = useSelector(selectedTemplateSelector);
    const selectedGallery: Template = useSelector(selectedGallerySelector);
    const importedTemplate: ImportedTemplate = useSelector(getImportedTemplate);
    const importedErrors = useSelector(getImportTemplateErrors);
    const importedValidationErrors = useSelector(getImportTemplateValidationErrors);
    const importType = useSelector(getImportTemplateType);
    const analysis = useSelector(getImportTemplateAnalysis);
    const targetDpi = useSelector(getImportTemplateTargetDpi);
    const { selectedFrameIds: selectedFrames } = useSelector(getFigmaState);
    const { addingInProgress: addingInProgressXML } = useSelector(getCreativesActionProgress);
    const { addingInProgress: addingInProgressDesign } = useSelector(getImportActionProgress);
    const maxDimensions = envVar.imageMaxDimensions;
    const isIndesignImport = isIndesignDesignFileAnalysis(analysis);
    const allDesignPagesOverMaxSize =
        importType === ImportType.DESIGN_FILE &&
        !!analysis?.designCreationResponses?.length &&
        analysis.designCreationResponses.every((artboard) => {
            if (isIndesignImport) {
                return isScaledArtboardOverMaxSize(artboard, targetDpi, maxDimensions);
            }

            return artboard.width > maxDimensions || artboard.height > maxDimensions;
        });

    const creationDisabledMap = {
        [DesignSubPages.ALL]: !selectedTemplate && !selectedGallery && !selectedType,
        [DesignSubPages.BLANK]: !selectedType,
        [DesignSubPages.CATEGORIES]: !selectedTemplate,
        [DesignSubPages.CATEGORY_DETAILS]: !selectedTemplate,
        [DesignSubPages.TEMPLATE_PREVIEW]: !selectedTemplate,
        [DesignSubPages.GALLERY]: !selectedGallery,
        [DesignSubPages.GALLERY_PREVIEW]: !selectedGallery,
        [DesignSubPages.IMPORT]: true,
        [DesignSubPages.IMPORT_STATUS]: (() => {
            if (importType === ImportType.XML) {
                return !importedTemplate || importedErrors || importedValidationErrors?.length || addingInProgressXML;
            }

            return (
                analysis?.designCreationResponses?.length === importedValidationErrors?.length ||
                addingInProgressDesign ||
                allDesignPagesOverMaxSize
            );
        })(),
        [DesignSubPages.FIGMA_IMPORT]: !selectedFrames.length,
    };
    const isCreationDisabled = () => creationDisabledMap[activePage] || false;
    const isImportDesignFileMaxSizeDisabled = () =>
        activePage === DesignSubPages.IMPORT_STATUS && allDesignPagesOverMaxSize;

    return { isCreationDisabled, isImportDesignFileMaxSizeDisabled };
};

export default useCreationDisabledMap;
